import { useState } from 'react';
import { Clinic, Appointment, Facility } from '@/services/features/appointmentSlotsService.ts';

export interface AppointmentSlotsState {
  // Data state
  clinics: Clinic[];
  appointments: Appointment[];
  originalAppointments: Appointment[]; // Store original unfiltered data
  facilities: Facility[];
  dialogFacilities: Facility[];

  // Search state
  searchBy: string;
  searchTerm: string;

  // Filter state
  selectedClinic: string;
  selectedClinicId: number | null;
  selectedFacility: string;
  selectedTimeSlot: string;
  facilitySearchQuery: string;
  filterClinicSearchQuery: string;

  // Dialog state
  isDialogOpen: boolean;
  dialogClinic: string;
  dialogClinicId: number | null;
  selectedDialogFacility: string;
  dialogFacilitySearchQuery: string;
  selectedDays: string[];
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  duration: string;
  clinicSearchQuery: string;
  validationErrors: Record<string, string>;

  // Filter dialog state
  isFilterDialogOpen: boolean;
  filterStartDate: Date | undefined;
  filterEndDate: Date | undefined;
  filterStartTime: string;
  filterEndTime: string;
  isFilterApplied: boolean;

  // Confirmation dialog states
  deleteConfirmDialog: {
    isOpen: boolean;
    appointmentId: number | null;
  };
  deleteAllConfirmDialog: boolean;
  filteredAppointmentsForDeletion: Appointment[] | null;

  // Pagination states
  currentPage: number;
  rowsPerPage: number;
  offset: number;
  hasMoreData: boolean;
  totalCount: number;

  // Loading states
  isLoading: boolean;
  isLoadingFacilities: boolean;
  isLoadingDialogFacilities: boolean;
  isCreatingSlots: boolean;
}

export const useAppointmentSlotsState = () => {
  // Data state
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [originalAppointments, setOriginalAppointments] = useState<Appointment[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [dialogFacilities, setDialogFacilities] = useState<Facility[]>([]);

  // Search state
  const [searchBy, setSearchBy] = useState<string>("clinic");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter state
  const [selectedClinic, setSelectedClinic] = useState<string>("");
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<string>("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
  const [facilitySearchQuery, setFacilitySearchQuery] = useState("");
  const [filterClinicSearchQuery, setFilterClinicSearchQuery] = useState("");

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogClinic, setDialogClinic] = useState("");
  const [dialogClinicId, setDialogClinicId] = useState<number | null>(null);
  const [selectedDialogFacility, setSelectedDialogFacility] = useState("");
  const [dialogFacilitySearchQuery, setDialogFacilitySearchQuery] = useState("");
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [duration, setDuration] = useState("");
  const [clinicSearchQuery, setClinicsSearchQuery] = useState("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Filter dialog state
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState<Date>();
  const [filterEndDate, setFilterEndDate] = useState<Date>();
  const [filterStartTime, setFilterStartTime] = useState("");
  const [filterEndTime, setFilterEndTime] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // Confirmation dialog states
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    appointmentId: number | null;
  }>({
    isOpen: false,
    appointmentId: null,
  });
  const [deleteAllConfirmDialog, setDeleteAllConfirmDialog] = useState(false);
  const [filteredAppointmentsForDeletion, setFilteredAppointmentsForDeletion] = useState<Appointment[] | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [offset, setOffset] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [isLoadingDialogFacilities, setIsLoadingDialogFacilities] = useState(false);
  const [isCreatingSlots, setIsCreatingSlots] = useState(false);

  // Reset functions
  const resetDialogState = () => {
    setDialogClinic("");
    setDialogClinicId(null);
    setSelectedDialogFacility("");
    setDialogFacilities([]);
    setSelectedDays([]);
    setStartDate(undefined);
    setEndDate(undefined);
    setStartTime("");
    setEndTime("");
    setDuration("");
    setClinicsSearchQuery("");
    setDialogFacilitySearchQuery("");
    setValidationErrors({});
  };

  const resetFilterState = () => {
    setSearchBy("clinic");
    setSearchTerm("");
    setSelectedClinic("");
    setSelectedClinicId(null);
    setSelectedFacility("");
    setFacilities([]);
    setFacilitySearchQuery("");
    setFilterClinicSearchQuery("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    setOffset(0);
    setFilterStartDate(undefined);
    setFilterEndDate(undefined);
    setFilterStartTime("");
    setFilterEndTime("");
  };

  const resetPagination = () => {
    setCurrentPage(1);
    setOffset(0);
  };

  return {
    // State values
    clinics,
    appointments,
    originalAppointments,
    facilities,
    dialogFacilities,
    searchBy,
    searchTerm,
    selectedClinic,
    selectedClinicId,
    selectedFacility,
    selectedTimeSlot,
    facilitySearchQuery,
    filterClinicSearchQuery,
    isDialogOpen,
    dialogClinic,
    dialogClinicId,
    selectedDialogFacility,
    dialogFacilitySearchQuery,
    selectedDays,
    startDate,
    endDate,
    startTime,
    endTime,
    duration,
    clinicSearchQuery,
    validationErrors,
    isFilterDialogOpen,
    filterStartDate,
    filterEndDate,
    filterStartTime,
    filterEndTime,
    isFilterApplied,
    deleteConfirmDialog,
    deleteAllConfirmDialog,
    filteredAppointmentsForDeletion,
    currentPage,
    rowsPerPage,
    offset,
    hasMoreData,
    totalCount,
    isLoading,
    isLoadingFacilities,
    isLoadingDialogFacilities,
    isCreatingSlots,

    // State setters
    setClinics,
    setAppointments,
    setOriginalAppointments,
    setFacilities,
    setDialogFacilities,
    setSearchBy,
    setSearchTerm,
    setSelectedClinic,
    setSelectedClinicId,
    setSelectedFacility,
    setSelectedTimeSlot,
    setFacilitySearchQuery,
    setFilterClinicSearchQuery,
    setIsDialogOpen,
    setDialogClinic,
    setDialogClinicId,
    setSelectedDialogFacility,
    setDialogFacilitySearchQuery,
    setSelectedDays,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    setDuration,
    setClinicsSearchQuery,
    setValidationErrors,
    setIsFilterDialogOpen,
    setFilterStartDate,
    setFilterEndDate,
    setFilterStartTime,
    setFilterEndTime,
    setIsFilterApplied,
    setDeleteConfirmDialog,
    setDeleteAllConfirmDialog,
    setFilteredAppointmentsForDeletion,
    setCurrentPage,
    setRowsPerPage,
    setOffset,
    setHasMoreData,
    setTotalCount,
    setIsLoading,
    setIsLoadingFacilities,
    setIsLoadingDialogFacilities,
    setIsCreatingSlots,

    // Reset functions
    resetDialogState,
    resetFilterState,
    resetPagination,
  };
};